import React, { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Highlight,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { t } from 'i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowRight,
  IconMailForward,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { useGetPatients } from '../../../global/entities/Patients/queries/useGetPatients.tsx';
import { patientStore } from '../../../global/Store/Store.ts';
import Utils from '../../../global/helpers/utils.ts';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../global/queryDefaults/QueryKeys.ts';
import { modals } from '@mantine/modals';
import { PatientInvitationsApi } from '../../../global/apis/PatientInvitationsApi/PatientInvitationsApi';
import { notifications } from '@mantine/notifications';
import { IPatient } from '../../../global/entities/Patients/Patient.ts';

const PatientTable: React.FC<{ hideTitle?: boolean }> = inject()(
  observer(({ hideTitle }: { hideTitle?: boolean }) => {
    const navigate = useNavigate();
    const { data, isLoading } = useGetPatients();
    const [search, setSearch] = useState('');
    const { ref, width } = useElementSize();
    const {
      sortedData,
      setSortedData,
      sortBy,
      setSorting,
      reverseSortDirection,
    } = useTable();
    const queryClient = useQueryClient();
    // Add media query hooks to detect small portrait screens
    const isSmallScreen = useMediaQuery('(max-width: 767px)');
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isTouchDevice = useMediaQuery('(pointer: coarse)');

    useEffect(() => {
      setSortedData(data ?? []);
    }, [data, setSortedData]);

    const onClickPatient = async (
      id: string,
      name: string,
      birthDate: Date | null,
      therapeuticIndicationId: string,
      sexId: string,
    ) => {
      try {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.tasks, patientStore.patientId],
        });

        await Promise.all([
          patientStore.setPatientId(id),
          patientStore.setPatientName(name),
          patientStore.setPatientBirthDate(birthDate?.toString() || ''),
          patientStore.setTherapeuticIndicationId(therapeuticIndicationId),
          patientStore.setSexId(sexId),
        ]);

        navigate('/hcpPatientDashboard');
      } catch (error) {
        console.error('Failed to set patient data:', error);
        notifications.show({
          message: t('notifications.errorLoadingPatient'),
          color: 'red',
        });
      }
    };

    const handlePatientInvite = () => {
      navigate('/hcpDashboard/patient/0');
    };

    const handleDeleteInvitation = (email: string, patientName: string) => {
      modals.openConfirmModal({
        title: t('page.patient.deleteInvitation'),
        children: (
          <p>
            {t('page.patient.deleteInvitationConfirm', {
              name: patientName,
              email: email,
            })}
          </p>
        ),
        labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            await PatientInvitationsApi.deletePatientInvitation(email);
            notifications.show({
              message: t('page.patient.invitationDeletedMessage'),
              color: 'green',
            });
            queryClient.invalidateQueries({ queryKey: [QueryKeys.patient] });
          } catch (error) {
            notifications.show({
              message: t('page.patient.invitationDeleteFailedMessage'),
              color: 'red',
            });
            console.error('Failed to delete invitation:', error);
          }
        },
      });
    };

    const handleResendInvitation = (
      email: string,
      patientName: string,
      isIncompleteRegistration: boolean,
    ) => {
      const titleKey = isIncompleteRegistration
        ? 'page.patient.sendRegistrationReminder'
        : 'page.patient.resendInvitation';
      const confirmKey = isIncompleteRegistration
        ? 'page.patient.sendRegistrationReminderConfirm'
        : 'page.patient.resendInvitationConfirm';
      const successKey = isIncompleteRegistration
        ? 'page.patient.registrationReminderSentMessage'
        : 'page.patient.invitationResentMessage';
      const failureKey = isIncompleteRegistration
        ? 'page.patient.registrationReminderSendFailedMessage'
        : 'page.patient.invitationResendFailedMessage';

      modals.openConfirmModal({
        title: t(titleKey),
        children: <p>{t(confirmKey, { name: patientName, email: email })}</p>,
        labels: { confirm: t('button.confirm'), cancel: t('button.cancel') },
        onConfirm: async () => {
          try {
            await PatientInvitationsApi.resendPatientInvitation(email);
            notifications.show({
              message: t(successKey),
              color: 'green',
            });
          } catch (error) {
            notifications.show({
              message: t(failureKey),
              color: 'red',
            });
            console.error('Failed to resend invitation:', error);
          }
        },
      });
    };

    const buttonStyle = {
      label: {
        display: 'inline-block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '36px',
      },
    };
    const isMobilePortrait = isSmallScreen && isPortrait && isTouchDevice;

    if (isLoading)
      return <Loading visible={true} loadingMessage={'Loading...'} />;

    const matchesSearch = (patient: IPatient) =>
      containsStringIgnoreCase(
        [`${patient.name}`, patient.fileNumber || ''],
        search,
      ) || !search;

    const filteredPatients = sortedData?.filter(matchesSearch);

    return (
      <Stack gap={'lg'} pt={hideTitle ? undefined : 'xl'} h={'100%'}>
        {!hideTitle && (
          <Title
            c="black"
            order={3}
            hidden={location.pathname.includes('main')}
          >
            {t('page.patient.patient')}
          </Title>
        )}
        <Paper w="100%" ref={ref}>
          <Stack gap={'md'} h={'100%'}>
            <Flex gap={'sm'} align={'center'}>
              <TextInput
                hidden={isMobilePortrait}
                placeholder={t('global.search')}
                rightSection={isMobilePortrait ? null : <IconSearch />}
                value={search}
                color={'var(--mantine-color-default-color)'}
                style={{ flex: 1 }}
                radius={'md'}
                onChange={(el) => setSearch(el.currentTarget.value)}
              />
              <Button
                onClick={handlePatientInvite}
                leftSection={<IconPlus />}
                styles={buttonStyle}
                w={isMobilePortrait ? '100%' : undefined}
              >
                {t('page.patient.invitePatient')}
              </Button>
            </Flex>

            {isMobilePortrait ? (
              <Stack gap="xs">
                {filteredPatients?.map((patient) => (
                  <Paper
                    key={patient.id}
                    withBorder
                    p="sm"
                    radius="md"
                    style={{
                      cursor: patient.isAccessible ? 'pointer' : 'default',
                    }}
                    onClick={
                      patient.isAccessible
                        ? () =>
                            onClickPatient(
                              patient.id.toString(),
                              patient.name,
                              patient.birthDate,
                              patient.therapeuticIndicationId.toString(),
                              patient.sexID.toString(),
                            )
                        : undefined
                    }
                  >
                    <Flex justify="space-between" align="flex-start" gap="xs">
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          size="sm"
                          fw={600}
                          c={
                            patient.isAccessible
                              ? 'var(--mantine-color-blue-light-color)'
                              : undefined
                          }
                          style={{ wordBreak: 'break-word' }}
                        >
                          {patient.fileNumber
                            ? `${patient.name} (${patient.fileNumber})`
                            : patient.name}
                        </Text>
                        <Flex gap="xs" align="center" wrap="wrap">
                          <Badge
                            size="xs"
                            variant="light"
                            color={patient.isPrimaryHcp ? 'blue' : 'gray'}
                          >
                            {patient.isPrimaryHcp
                              ? t('table.patient.primary')
                              : t('table.patient.secondary')}
                          </Badge>
                          <Badge size="xs" variant="light" color="gray">
                            {patient.status}
                          </Badge>
                          {patient.isAccessible && patient.birthDate && (
                            <Text size="xs" c="dimmed">
                              {Utils.formatDateToYYYYMMDD(patient.birthDate)}
                            </Text>
                          )}
                        </Flex>
                      </Stack>
                      {!patient.isAccessible ? (
                        <Flex gap={4}>
                          <Tooltip
                            label={t(
                              patient.isDeletable
                                ? 'page.patient.resendInvitation'
                                : 'page.patient.sendRegistrationReminder',
                            )}
                          >
                            <ActionIcon
                              variant="transparent"
                              color="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResendInvitation(
                                  patient.email,
                                  patient.name,
                                  !patient.isDeletable,
                                );
                              }}
                            >
                              <IconMailForward size={18} />
                            </ActionIcon>
                          </Tooltip>
                          <ActionIcon
                            variant="transparent"
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteInvitation(
                                patient.email,
                                patient.name,
                              );
                            }}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Flex>
                      ) : (
                        <ActionIcon
                          variant="transparent"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (patient.isAccessible) {
                              onClickPatient(
                                patient.id.toString(),
                                patient.name,
                                patient.birthDate,
                                patient.therapeuticIndicationId.toString(),
                                patient.sexID.toString(),
                              );
                            }
                          }}
                        >
                          <IconArrowRight
                            color={theme.colors?.blue?.toString()}
                          />
                        </ActionIcon>
                      )}
                    </Flex>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <ScrollArea w="100%">
                <Table
                  verticalSpacing="xs"
                  mt={'xs'}
                  style={{ tableLayout: 'auto' }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Th
                        sorted={sortBy === 'name'}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting('name', sortedData)}
                      >
                        {t('global.patientName')}
                      </Th>
                      <Th
                        sorted={sortBy === 'fileNumber'}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting('fileNumber', sortedData)}
                        hidden={width <= 450}
                      >
                        {t('formGlobal.fileNumber')}
                      </Th>
                      <Th
                        sorted={sortBy === 'isPrimaryHcp'}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting('isPrimaryHcp', sortedData)}
                        hidden={width <= 450}
                      >
                        {t('table.patient.role')}
                      </Th>
                      <Th
                        sorted={sortBy === 'birthDate'}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting('birthDate', sortedData)}
                      >
                        {t('formProfile.birthDate')}
                      </Th>
                      <Th
                        hidden={width <= 300}
                        sorted={sortBy === 'status'}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting('status', sortedData)}
                      >
                        {t('table.patient.profileStatus')}
                      </Th>
                      <Table.Th hidden={width <= 450}>
                        {t('global.email')}
                      </Table.Th>
                      <Table.Th style={{ width: 'min-content' }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortedData?.map((patient, index: number) => {
                      const isSearch = matchesSearch(patient);
                      return (
                        <Table.Tr
                          hidden={!isSearch}
                          key={index}
                          style={{
                            background:
                              index % 2 === 1
                                ? undefined
                                : 'var(--Background-Gray-light, #E9ECEF)',
                          }}
                        >
                          <Table.Td
                            style={{
                              maxWidth: 120,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: patient.isAccessible
                                ? 'pointer'
                                : 'default',
                            }}
                            onClick={
                              patient.isAccessible
                                ? () =>
                                    onClickPatient(
                                      patient.id.toString(),
                                      patient.name,
                                      patient.birthDate,
                                      patient.therapeuticIndicationId.toString(),
                                      patient.sexID.toString(),
                                    )
                                : undefined
                            }
                          >
                            <Highlight
                              highlight={search}
                              highlightStyles={{
                                color: 'var(--mantine-color-gray-9)',
                                fontWeight: 700,
                                WebkitBackgroundClip: 'text',
                              }}
                              styles={{
                                root: {
                                  fontSize: 14,
                                  fontWeight: patient.isAccessible ? 550 : 500,
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  color: patient.isAccessible
                                    ? 'var(--mantine-color-blue-light-color)'
                                    : undefined,
                                },
                              }}
                              truncate
                            >
                              {patient.name}
                            </Highlight>
                          </Table.Td>
                          <Table.Td
                            hidden={width <= 450}
                            style={{
                              maxWidth: 120,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {patient.fileNumber || '-'}
                          </Table.Td>
                          <Table.Td hidden={width <= 450}>
                            <Badge
                              variant="light"
                              color={patient.isPrimaryHcp ? 'blue' : 'gray'}
                              size="sm"
                            >
                              {patient.isPrimaryHcp
                                ? t('table.patient.primary')
                                : t('table.patient.secondary')}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {patient.isAccessible && patient.birthDate
                              ? Utils.formatDateToYYYYMMDD(patient.birthDate)
                              : '-'}
                          </Table.Td>
                          <Table.Td hidden={width <= 300}>
                            {patient.status}
                          </Table.Td>
                          <Table.Td
                            hidden={width <= 450}
                            style={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {patient.email}
                          </Table.Td>
                          <Table.Td style={{ width: 'min-content' }}>
                            <Flex
                              justify="flex-end"
                              gap={4}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {!patient.isAccessible ? (
                                <>
                                  <Tooltip
                                    label={t(
                                      patient.isDeletable
                                        ? 'page.patient.resendInvitation'
                                        : 'page.patient.sendRegistrationReminder',
                                    )}
                                  >
                                    <ActionIcon
                                      variant={'transparent'}
                                      color="blue"
                                      onClick={() =>
                                        handleResendInvitation(
                                          patient.email,
                                          patient.name,
                                          !patient.isDeletable,
                                        )
                                      }
                                    >
                                      <IconMailForward size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip
                                    label={t('page.patient.cancelRegistration')}
                                  >
                                    <ActionIcon
                                      variant={'transparent'}
                                      color="red"
                                      onClick={() =>
                                        handleDeleteInvitation(
                                          patient.email,
                                          patient.name,
                                        )
                                      }
                                    >
                                      <IconTrash size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              ) : (
                                <ActionIcon
                                  variant={'transparent'}
                                  onClick={() =>
                                    onClickPatient(
                                      patient.id.toString(),
                                      patient.name,
                                      patient.birthDate,
                                      patient.therapeuticIndicationId.toString(),
                                      patient.sexID.toString(),
                                    )
                                  }
                                >
                                  <IconArrowRight
                                    color={theme.colors?.blue?.toString()}
                                  />
                                </ActionIcon>
                              )}
                            </Flex>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  }),
);

export const containsStringIgnoreCase = (
  array: string[],
  searchString: string,
): boolean => {
  return array.some((item) =>
    item?.toLowerCase().includes(searchString.toLowerCase()),
  );
};

const WrappedPatientTable: React.FC<{ hideTitle?: boolean }> = ({
  hideTitle,
}) => {
  return (
    <TableContextProvider>
      <PatientTable hideTitle={hideTitle} />
    </TableContextProvider>
  );
};

export default WrappedPatientTable;
